/**
 * RescheduleEscalationsPanel (06/2026, Task C) — zeigt eskalierte WhatsApp-
 * Verschiebe-Anfragen: Der Kunde hat eine NICHT angebotene Uhrzeit gewünscht.
 * Der Admin kann neue Zeiten anbieten oder „keine Slots frei" melden.
 */
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { AlertTriangle, Loader2, CalendarClock, X } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const RescheduleEscalationsPanel = ({ onOfferNewSlots, refreshSignal }) => {
  const [offers, setOffers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(null);

  const authHeaders = { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/whatsapp/reschedule/offers`, {
        ...authHeaders, params: { status: 'escalated' },
      });
      setOffers(res.data?.offers || []);
    } catch {
      setOffers([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, [load, refreshSignal]);

  const resolve = async (offer, action) => {
    setBusy(offer.id);
    try {
      await axios.post(`${API}/whatsapp/reschedule/offers/${offer.id}/resolve`, { action }, authHeaders);
      toast.success(action === 'no_slots' ? 'Kunde informiert: keine Slots frei' : 'Anfrage geschlossen');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Aktion fehlgeschlagen');
    } finally {
      setBusy(null);
    }
  };

  if (!loading && offers.length === 0) return null;

  return (
    <div className="mb-4 rounded-lg border border-amber-300 bg-amber-50 p-3" data-testid="reschedule-escalations">
      <div className="flex items-center gap-2 mb-2 text-amber-800 font-medium text-sm">
        <AlertTriangle size={16} />
        Offene Verschiebe-Anfragen ({offers.length})
      </div>
      <div className="space-y-2">
        {offers.map((o) => (
          <div key={o.id} className="flex flex-wrap items-center justify-between gap-2 rounded bg-white border border-amber-200 px-3 py-2 text-sm"
               data-testid={`escalation-${o.id}`}>
            <div className="flex items-center gap-2 min-w-0">
              <CalendarClock size={14} className="text-amber-500 shrink-0" />
              <span className="font-medium truncate">{o.customer_name || o.phone}</span>
              <span className="text-gray-500">wünscht</span>
              <span className="font-semibold text-amber-700">{o.requested_time || o.reply_text}</span>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => onOfferNewSlots?.(o.appointment_id)}
                className="rounded bg-emerald-600 hover:bg-emerald-700 text-white px-2 py-1 text-xs"
                data-testid={`escalation-offer-${o.id}`}
              >
                Neue Zeiten anbieten
              </button>
              <button
                onClick={() => resolve(o, 'no_slots')}
                disabled={busy === o.id}
                className="rounded bg-gray-100 hover:bg-gray-200 text-gray-700 px-2 py-1 text-xs"
                data-testid={`escalation-noslots-${o.id}`}
              >
                {busy === o.id ? <Loader2 className="animate-spin" size={12} /> : 'Keine Slots frei'}
              </button>
              <button
                onClick={() => resolve(o, 'cancel')}
                disabled={busy === o.id}
                className="text-gray-400 hover:text-gray-600"
                title="Anfrage schliessen"
                data-testid={`escalation-cancel-${o.id}`}
              >
                <X size={14} />
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default RescheduleEscalationsPanel;
